'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Infrastructure misconfiguration prevention',
    description:
      'Terraform, Pulumi, CloudFormation, and Ansible code scanned before apply. Privilege escalations, public exposure, and policy violations caught at the PR stage, before any environment is affected.',
  },
  {
    tag: '[02]',
    title: 'Pipeline secrets and supply chain security',
    description:
      'Secrets detection across your entire codebase and pipeline history. SBOM generation for every build. Dependency signing and provenance verification to protect against supply chain compromise.',
  },
  {
    tag: '[03]',
    title: 'Policy-as-code enforcement',
    description:
      'Custom OPA policies and Checkov rulesets enforcing your organisation\'s security standards across all infrastructure code. Policies version-controlled, reviewed, and enforced consistently.',
  },
  {
    tag: '[04]',
    title: 'Drift detection and automated remediation',
    description:
      'Infrastructure that drifts from its defined state is detected and flagged immediately. Automated remediation for known-safe corrections; escalation for changes requiring human review.',
  },
];

const outcomes = [
  { metric: 'Pre-apply', label: 'Misconfigurations caught before any environment is affected' },
  { metric: '100%', label: 'IaC coverage, every resource type, every provider' },
  { metric: '< 5min', label: 'Pipeline security gate execution time' },
  { metric: 'Zero', label: 'Secrets committed to version control' },
];

const techStack = [
  { category: 'IaC Platforms', tools: ['Terraform', 'Pulumi', 'CloudFormation', 'Ansible', 'Helm'] },
  { category: 'IaC Security', tools: ['Checkov', 'tfsec', 'Terrascan', 'KICS'] },
  { category: 'Policy-as-Code', tools: ['OPA / Rego', 'Sentinel', 'Custom Checkov rules'] },
  { category: 'Secrets Detection', tools: ['Gitleaks', 'TruffleHog', 'detect-secrets'] },
  { category: 'SBOM & Supply Chain', tools: ['Syft', 'Grype', 'Sigstore / Cosign', 'SLSA'] },
  { category: 'Pipeline Platforms', tools: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'Azure DevOps', 'Tekton'] },
];

export default function IaCPipelinePage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '500px', height: '500px', top: '-100px', right: '10%', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '450px', height: '450px', bottom: '-100px', left: '-50px', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">IaC & Pipeline Security</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [IaC & Pipeline Security]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Secure infrastructure<br />
              <span className="text-[#80edfa]">before it is ever provisioned.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Misconfigured infrastructure is one of the most common causes of cloud security incidents. We harden your CI/CD pipelines and secure your Infrastructure as Code, catching policy violations, privilege escalations, and misconfigurations before they reach any environment. Prevention, not remediation.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Talk to an IaC security engineer</a>
              <a href="#use-cases" className="btn-secondary">See use cases</a>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Use cases</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Shift-left security for infrastructure
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                Infrastructure security cannot be an afterthought. By the time a misconfiguration reaches production, the blast radius is already significant. TechSonar secures infrastructure at the code level, before it is ever applied.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Stack */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Technical depth</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              The tooling stack behind the programme
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {techStack?.map((t, i) => (
              <div key={i} className="border border-[#222] bg-[#181b1f] p-6 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{t?.category}</p>
                <div className="flex flex-wrap gap-2">
                  {t?.tools?.map((tool, j) => (
                    <span key={j} className="text-xs text-[#aaa] border border-[#333] px-3 py-1 font-mono-brand">{tool}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pipeline Security Detail */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="lg:w-1/2 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Pipeline hardening</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
                Every stage of the pipeline, secured
              </h2>
              <p className="text-[#aaa] text-base leading-relaxed mb-8">
                A CI/CD pipeline is an attack surface. Compromised pipelines have been the vector for some of the most significant supply chain attacks in recent years. TechSonar hardens every stage, from source control to deployment, with security controls that do not slow delivery.
              </p>
              <ul className="flex flex-col gap-4">
                {[
                  'Pre-commit hooks: secrets detection and IaC linting',
                  'PR security gates: SAST, SCA, and IaC scanning',
                  'Build hardening: minimal base images, reproducible builds',
                  'SBOM generation and artifact signing on every build',
                  'Deployment gates: policy validation before any environment',
                  'Runtime drift detection and automated remediation',
                ]?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <img src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg" alt="" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#aaa]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-1/2 flex flex-col gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Policy-as-code approach</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Security policies are code. Version-controlled, peer-reviewed, and enforced consistently across every environment. OPA and Checkov rulesets codify your organisation's security standards, so enforcement is automatic, not manual.
                </p>
              </div>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Supply chain security</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  SBOM generation, artifact signing with Sigstore/Cosign, and SLSA provenance attestation. Your software supply chain is documented, signed, and verifiable, meeting the requirements of NIST SSDF, EO 14028, and emerging regulatory frameworks.
                </p>
              </div>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">No delivery impact</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  Security gates are designed to run fast. Parallel execution, incremental scanning, and intelligent caching mean security checks add minutes, not hours, to your pipeline. Security that keeps pace with your delivery cadence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Secure your infrastructure before it ships.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
